import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import type {LazyLoadingGetterParams, LazyLoadingResponse} from '@Libraries/pmw-stock-media-library';
import type {GenerateAISpeechResponse} from '@Libraries/text-to-speech.library';
import {getGridItemForGeneratedSpeech, getUserGeneratedSpeech} from '@Libraries/text-to-speech.library';
import {LazyMasonryGrid} from '@Components/lazy-masonry-grid';
import type {LazyMasonryGridProps} from '@Components/lazy-masonry-grid/lazy-masonry-grid';
import styles from './recent-generated-voice-view.module.scss';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';

interface RecentGeneratedVoiceViewProps extends Partial<LazyMasonryGridProps> {
  gridId: string;
}

export default function RecentGeneratedVoiceView({...props}: RecentGeneratedVoiceViewProps): ReactElement {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const {topShadow, bottomShadow} = useTopBottomScrollShadow(gridRef);
  const getItems = async (params: LazyLoadingGetterParams): Promise<LazyLoadingResponse> => {
    const offset = Object.keys(params.offsets).reduce((agg, item) => {
      return agg + params.offsets[item];
    }, 0);

    let items: GenerateAISpeechResponse[] = [];

    try {
      items = await getUserGeneratedSpeech(offset, params.limit);
    } catch (e) {
      items = [];
    }

    const gridItems = items.map((item) => {
      return {
        ...getGridItemForGeneratedSpeech(item),
        ...(props.customDimensions ? {gridItemHeight: props.customDimensions.height} : {}),
      };
    });

    return {response: gridItems, offsetsData: {DEFAULT_SOURCE: gridItems.length}};
  };

  return (
    <LazyMasonryGrid
      {...props}
      gridRef={(ref) => {
        gridRef.current = ref;
        if (props.gridRef) {
          props.gridRef(ref);
        }
      }}
      clearGridOnUnmount={props.clearGridOnUnmount ?? true}
      getItems={props.getItems ?? getItems}
      gridSelectionGroupId={props.gridSelectionGroupId ?? props.gridId}
      className={`_full-width _full-height ${styles.grid} ${props.className ?? ''}`}
      gridContainerClassName={`${topShadow ? styles.gridTopShadow : ''} ${bottomShadow ? styles.gridBottomShadow : ''}`}
    />
  );
}
